/**
 * Organization API - Simplified
 * Maps to Services-organizations/Service.api via Gateway
 * 
 * Industry Standard:
 * - All requests go through Gateway with httpOnly cookies
 * - No token management needed in frontend
 * - Single client handles all requests
 */

import apiClient from './client';
import type {
  Church,
  ChurchCreateRequest,
  ChurchUpdateRequest,
  Song,
  SongUsageRecomputeResult,
  CreateSongDto,
  UpdateSongDto,
  Membership,
  EnrichedMembership,
  GroupMembershipCreateRequest,
  ServicePlan,
  CreateServicePlanDto,
  UpdateServicePlanDto,
  Group,
  GroupCreateRequest,
  GroupUpdateRequest,
  GroupInviteCodeResponse,
  SetlistTemplate,
  CreateSetlistTemplateDto,
  UpdateSetlistTemplateDto,
  MemberAvailabilityDto,
  AvailabilityStatusValue,
} from '../types';

// Convenience wrapper for creating churches
export const organizationApi = {
  createChurch: (request: ChurchCreateRequest, onboardingToken?: string) => 
    churchesApi.create(request, onboardingToken),
};

// ----------------------------------------------------------------------------
// CHURCH ENDPOINTS
// ----------------------------------------------------------------------------

export const churchesApi = {
  /**
   * Create a new church
   * POST /api/organizations/churches
   */
  create: async (request: ChurchCreateRequest, onboardingToken?: string): Promise<Church> => {
    const config: any = {};
    if (onboardingToken) {
      config.headers = { Authorization: `Bearer ${onboardingToken}` };
    }
    const response = await apiClient.post<Church>('/api/organizations/churches', request, config);
    return response.data;
  },

  /**
   * Get church by ID
   * GET /api/organizations/churches/{id}
   */
  getById: async (id: string): Promise<Church> => {
    const response = await apiClient.get<Church>(`/api/organizations/churches/${id}`);
    return response.data;
  },

  /**
   * Update church details
   * PUT /api/organizations/churches/{id}
   */
  update: async (id: string, request: ChurchUpdateRequest, updatedBy: string): Promise<void> => {
    await apiClient.put(`/api/organizations/churches/${id}?updatedBy=${updatedBy}`, request);
  },

  /**
   * Delete church (soft delete)
   * DELETE /api/organizations/churches/{id}
   */
  delete: async (id: string, deletedBy: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/churches/${id}?deletedBy=${deletedBy}`);
  },

  /**
   * Restore a soft-deleted church.
   * POST /api/organizations/churches/{id}/restore
   */
  restore: async (id: string): Promise<void> => {
    await apiClient.post(`/api/organizations/churches/${id}/restore`);
  },

  /**
   * Permanently delete an already soft-deleted church and all of its data.
   * Caller MUST first call delete() to soft-delete; the backend rejects this
   * call when the church is still active.
   * DELETE /api/organizations/churches/{id}/permanent
   */
  permanentlyDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/churches/${id}/permanent`);
  },

  /**
   * Generate an invite code for a church
   * POST /api/organizations/churches/{churchId}/invite-code
   */
  generateInviteCode: async (churchId: string, expiryDays: number = 7): Promise<{ inviteCode: string; expiresAt: string }> => {
    const response = await apiClient.post(`/api/organizations/churches/${churchId}/invite-code?expiryDays=${expiryDays}`);
    return response.data;
  },

  /**
   * Join a church using an invite code
   * POST /api/organizations/churches/join-by-code
   */
  joinByInviteCode: async (inviteCode: string, userId: string): Promise<Membership> => {
    const response = await apiClient.post('/api/organizations/churches/join-by-code', { inviteCode, userId });
    return response.data;
  },
};

// ----------------------------------------------------------------------------
// SONG ENDPOINTS
// ----------------------------------------------------------------------------

export const songsApi = {
  /**
   * Get songs from church library
   * GET /api/organizations/churches/{churchId}/songs
   */
  getLibrary: async (
    churchId: string,
    search?: string,
    tags?: string[],
    limit: number = 100,
    options?: { signal?: AbortSignal },
    groupId?: string
  ): Promise<Song[]> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tags && tags.length > 0) params.append('tags', tags.join(','));
    params.append('limit', limit.toString());
    if (groupId) params.append('groupId', groupId);

    const config: any = {};
    if (options?.signal) config.signal = options.signal;

    const response = await apiClient.get<Song[]>(
      `/api/organizations/churches/${churchId}/songs?${params.toString()}`,
      config
    );
    return response.data;
  },

  /**
   * Get a specific song by ID
   * GET /api/organizations/churches/{churchId}/songs/{songId}
   */
  getById: async (churchId: string, songId: string): Promise<Song> => {
    const response = await apiClient.get<Song>(`/api/organizations/churches/${churchId}/songs/${songId}`);
    return response.data;
  },

  /**
   * Create a new song
   * POST /api/organizations/churches/{churchId}/songs
   */
  create: async (churchId: string, request: CreateSongDto): Promise<Song> => {
    const response = await apiClient.post<Song>(`/api/organizations/churches/${churchId}/songs`, request);
    return response.data;
  },

  /**
   * Update a song
   * PUT /api/organizations/churches/{churchId}/songs/{songId}
   */
  update: async (churchId: string, songId: string, request: UpdateSongDto): Promise<Song> => {
    const response = await apiClient.put<Song>(`/api/organizations/churches/${churchId}/songs/${songId}`, request);
    return response.data;
  },

  /**
   * Delete a song (soft delete)
   * DELETE /api/organizations/churches/{churchId}/songs/{songId}
   */
  delete: async (churchId: string, songId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/churches/${churchId}/songs/${songId}`);
  },

  /**
   * Batch import songs with duplicate detection
   * POST /api/organizations/churches/{churchId}/songs/batch
   */
  batchCreate: async (
    churchId: string,
    songs: CreateSongDto[]
  ): Promise<{ created: Song[]; duplicates: Song[]; failed: Array<{ song: CreateSongDto; error: string }> }> => {
    const response = await apiClient.post(`/api/organizations/churches/${churchId}/songs/batch`, songs);
    return response.data;
  },

  /**
   * Recompute play counts from all completed services (church admin only).
   * Songs sung but missing from the library are added, within the song limit.
   * POST /api/organizations/churches/{churchId}/songs/usage/recompute
   */
  recomputeUsage: async (churchId: string): Promise<SongUsageRecomputeResult> => {
    const response = await apiClient.post<SongUsageRecomputeResult>(
      `/api/organizations/churches/${churchId}/songs/usage/recompute`
    );
    return response.data;
  },
};

// ----------------------------------------------------------------------------
// MEMBERSHIP ENDPOINTS
// ----------------------------------------------------------------------------

export const membershipsApi = {
  /**
   * Add a user to a group
   * POST /api/organizations/memberships/group
   */
  addGroupMember: async (request: GroupMembershipCreateRequest): Promise<Membership> => {
    const response = await apiClient.post<Membership>('/api/organizations/memberships/group', request);
    return response.data;
  },

  /**
   * Get all members of a church
   * GET /api/organizations/memberships/church/{churchId}
   */
  getChurchMembers: async (churchId: string): Promise<EnrichedMembership[]> => {
    const response = await apiClient.get<EnrichedMembership[]>(`/api/organizations/memberships/church/${churchId}`);
    return response.data;
  },

  /**
   * Get pending membership requests for a church
   * GET /api/organizations/memberships/pending/{churchId}
   */
  getPendingMembers: async (churchId: string): Promise<EnrichedMembership[]> => {
    const response = await apiClient.get<EnrichedMembership[]>(`/api/organizations/memberships/pending/${churchId}`);
    return response.data;
  },

  /**
   * Update a membership
   * PUT /api/organizations/memberships/{membershipId}
   */
  updateMembership: async (membershipId: string, request: any): Promise<Membership> => {
    const response = await apiClient.put<Membership>(`/api/organizations/memberships/${membershipId}`, request);
    return response.data;
  },

  /**
   * Approve a pending membership
   * PUT /api/organizations/memberships/{membershipId}/approve
   */
  approveMembership: async (membershipId: string, approvedBy: string): Promise<Membership> => {
    const response = await apiClient.put<Membership>(`/api/organizations/memberships/${membershipId}/approve`, { approvedBy });
    return response.data;
  },

  /**
   * Remove user from church
   * DELETE /api/organizations/memberships/church/{churchId}/user/{userId}
   */
  removeFromChurch: async (churchId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/memberships/church/${churchId}/user/${userId}`);
  },

  /**
   * Get all memberships for a user
   * GET /api/organizations/memberships/user/{userId}
   */
  getUserMemberships: async (userId: string): Promise<Membership[]> => {
    const response = await apiClient.get<Membership[]>(`/api/organizations/memberships/user/${userId}`);
    return response.data;
  },

  /**
   * Get all members of a specific group
   * GET /api/organizations/memberships/group/{groupId}
   */
  getGroupMembers: async (groupId: string): Promise<Membership[]> => {
    const response = await apiClient.get<Membership[]>(`/api/organizations/memberships/group/${groupId}`);
    return response.data;
  },

  /**
   * Remove user from group
   * DELETE /api/organizations/memberships/group/{groupId}/user/{userId}
   */
  removeFromGroup: async (groupId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/memberships/group/${groupId}/user/${userId}`);
  },
};

// ----------------------------------------------------------------------------
// SERVICE PLAN ENDPOINTS
// ----------------------------------------------------------------------------

// Map backend PlanItem field names to/from admin frontend field names.
// Backend uses `link` for YouTube URLs; admin frontend uses `youtubeUrl`.
// Backend serializes AssignmentStatus as strings ("Pending", "Confirmed", "Declined")
// via JsonStringEnumConverter; frontend compares as numbers (0, 1, 2).
const STATUS_MAP: Record<string, number> = { Pending: 0, Confirmed: 1, Declined: 2 };

function mapPlanFromApi(plan: ServicePlan): ServicePlan {
  return {
    ...plan,
    items: (plan.items || []).map((item: any, index: number) => ({
      ...item,
      id: item.id || `item-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
      type: item.type || (item.songId || item.link ? 'Song' : 'Other'),
      youtubeUrl: item.youtubeUrl || item.link || undefined,
    })),
    team: (plan.team || []).map((m: any) => ({
      ...m,
      status: typeof m.status === 'string' ? (STATUS_MAP[m.status] ?? 0) : m.status,
    })),
  };
}

function mapItemsToApi(items: any[]): any[] {
  return items.map(({ youtubeUrl, ...rest }) => ({
    ...rest,
    link: youtubeUrl || rest.link || undefined,
  }));
}

export const plansApi = {
  /**
   * Get upcoming service plan
   * GET /api/organizations/churches/{churchId}/plans/upcoming
   */
  getUpcoming: async (churchId: string, groupId?: string): Promise<ServicePlan | null> => {
    try {
      const params = groupId ? `?groupId=${groupId}` : '';
      const response = await apiClient.get<ServicePlan>(`/api/organizations/churches/${churchId}/plans/upcoming${params}`);
      return mapPlanFromApi(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  /**
   * Get recent service plans
   * GET /api/organizations/churches/{churchId}/plans/recent
   */
  getRecent: async (churchId: string, limit: number = 5, groupId?: string): Promise<ServicePlan[]> => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (groupId) params.append('groupId', groupId);
    const response = await apiClient.get<ServicePlan[]>(
      `/api/organizations/churches/${churchId}/plans/recent?${params.toString()}`
    );
    return response.data.map(mapPlanFromApi);
  },

  /**
   * Get specific service plan
   * GET /api/organizations/churches/{churchId}/plans/{planId}
   */
  getById: async (churchId: string, planId: string): Promise<ServicePlan> => {
    const response = await apiClient.get<ServicePlan>(`/api/organizations/churches/${churchId}/plans/${planId}`);
    return mapPlanFromApi(response.data);
  },

  /**
   * Create a new service plan
   * POST /api/organizations/churches/{churchId}/plans
   */
  create: async (churchId: string, request: CreateServicePlanDto): Promise<ServicePlan> => {
    const payload = request.items ? { ...request, items: mapItemsToApi(request.items) } : request;
    const response = await apiClient.post<ServicePlan>(`/api/organizations/churches/${churchId}/plans`, payload);
    return mapPlanFromApi(response.data);
  },

  /**
   * Update service plan
   * PUT /api/organizations/churches/{churchId}/plans/{planId}
   */
  update: async (churchId: string, planId: string, request: UpdateServicePlanDto): Promise<ServicePlan> => {
    const payload = request.items ? { ...request, items: mapItemsToApi(request.items) } : request;
    const response = await apiClient.put<ServicePlan>(`/api/organizations/churches/${churchId}/plans/${planId}`, payload);
    return mapPlanFromApi(response.data);
  },

  /**
   * Reschedule a service plan to a new date.
   * - Past plans are cloned (new id, fresh practice records, team statuses
   *   reset to Pending, isSetlistPublished=false). Original is preserved.
   * - Future plans are moved in place (team statuses reset to Pending).
   * POST /api/organizations/churches/{churchId}/plans/{planId}/reschedule
   */
  reschedule: async (
    churchId: string,
    planId: string,
    newDate: string,
  ): Promise<{ plan: ServicePlan; wasCloned: boolean; originalPlanId: string | null }> => {
    const response = await apiClient.post<{
      plan: ServicePlan;
      wasCloned: boolean;
      originalPlanId: string | null;
    }>(
      `/api/organizations/churches/${churchId}/plans/${planId}/reschedule`,
      { newDate },
    );
    return {
      plan: mapPlanFromApi(response.data.plan),
      wasCloned: response.data.wasCloned,
      originalPlanId: response.data.originalPlanId,
    };
  },

  /**
   * Delete service plan (soft delete)
   * DELETE /api/organizations/churches/{churchId}/plans/{planId}
   */
  delete: async (churchId: string, planId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/churches/${churchId}/plans/${planId}`);
  },

  /**
   * Re-emit a plan notification that never reached its recipients.
   * POST /api/organizations/churches/{churchId}/plans/{planId}/notifications/replay
   * Admin-only. Not idempotent downstream: each call sends again.
   */
  replayNotification: async (
    churchId: string,
    planId: string,
    eventType: 'plan.published' | 'plan.roster_updated' | 'plan.setlist_lyrics',
  ): Promise<{ sent: boolean; reason?: string; recipientCount?: number; songCount?: number }> => {
    const response = await apiClient.post<{
      sent: boolean;
      reason?: string;
      recipientCount?: number;
      songCount?: number;
    }>(
      `/api/organizations/churches/${churchId}/plans/${planId}/notifications/replay?eventType=${encodeURIComponent(eventType)}`,
    );
    return response.data;
  },

  /**
   * Update team member assignment status (Pending/Confirmed/Declined)
   * PATCH /api/organizations/churches/{churchId}/plans/{planId}/team/{userId}/status
   */
  updateTeamMemberStatus: async (churchId: string, planId: string, userId: string, status: number): Promise<void> => {
    await apiClient.patch(
      `/api/organizations/churches/${churchId}/plans/${planId}/team/${userId}/status`,
      { status }
    );
  },

  /**
   * Update service plan roster only (preserves setlist items)
   * PATCH /api/organizations/churches/{churchId}/plans/{planId}/roster
   */
  updateRoster: async (churchId: string, planId: string, request: {
    date: string;
    title: string;
    notes: string;
    duration?: string;
    serviceType?: string;
    items?: any[];
    team: any[];
    isPublished: boolean;
    isSetlistPublished?: boolean;
  }): Promise<ServicePlan> => {
    const payload = request.items ? { ...request, items: mapItemsToApi(request.items) } : request;
    const response = await apiClient.patch<ServicePlan>(
      `/api/organizations/churches/${churchId}/plans/${planId}/roster`,
      payload
    );
    return mapPlanFromApi(response.data);
  },

  /**
   * Migrate orphaned plans (no groupId) to a target group
   * POST /api/organizations/churches/{churchId}/plans/migrate-orphans
   */
  migrateOrphanedPlans: async (churchId: string, targetGroupId: string): Promise<{ migrated: number; targetGroupId: string; targetGroupName: string }> => {
    const response = await apiClient.post(`/api/organizations/churches/${churchId}/plans/migrate-orphans`, { targetGroupId });
    return response.data;
  },

  /**
   * Reassign a single plan's group (admin-only correction).
   * PATCH /api/organizations/churches/{churchId}/plans/{planId}/group
   */
  reassignGroup: async (churchId: string, planId: string, groupId: string): Promise<ServicePlan> => {
    const response = await apiClient.patch<ServicePlan>(
      `/api/organizations/churches/${churchId}/plans/${planId}/group`,
      { groupId },
    );
    return mapPlanFromApi(response.data);
  },

  /**
   * List the church's published past setlists (admin view, group-agnostic).
   * Reuses the member-facing endpoint but without a group filter.
   * GET /api/organizations/churches/{churchId}/plans/published-history
   */
  listPublishedHistory: async (churchId: string, limit: number = 100): Promise<ServicePlan[]> => {
    const response = await apiClient.get<ServicePlan[]>(
      `/api/organizations/churches/${churchId}/plans/published-history?limit=${limit}`
    );
    return response.data.map(mapPlanFromApi);
  },

  /**
   * List all upcoming service plans for the church (admin view, group-agnostic).
   * GET /api/organizations/churches/{churchId}/plans/upcoming-all
   */
  listUpcomingAll: async (churchId: string, limit: number = 100): Promise<ServicePlan[]> => {
    const response = await apiClient.get<ServicePlan[]>(
      `/api/organizations/churches/${churchId}/plans/upcoming-all?limit=${limit}`
    );
    return response.data.map(mapPlanFromApi);
  },
};

// ----------------------------------------------------------------------------
// GROUP ENDPOINTS
// ----------------------------------------------------------------------------

export const groupsApi = {
  /**
   * Get all groups for a church
   * GET /api/organizations/groups/church/{churchId}
   */
  getByChurch: async (churchId: string): Promise<Group[]> => {
    const response = await apiClient.get<Group[]>(`/api/organizations/groups/church/${churchId}`);
    return response.data;
  },

  /**
   * Get a specific group
   * GET /api/organizations/groups/{groupId}
   */
  getById: async (groupId: string): Promise<Group> => {
    const response = await apiClient.get<Group>(`/api/organizations/groups/${groupId}`);
    return response.data;
  },

  /**
   * Create a new group
   * POST /api/organizations/groups
   */
  create: async (_churchId: string, request: GroupCreateRequest): Promise<Group> => {
    const response = await apiClient.post<{ group: Group }>(`/api/organizations/groups`, request);
    return response.data.group;
  },

  /**
   * Update a group
   * PUT /api/organizations/groups/{groupId}
   */
  update: async (groupId: string, request: GroupUpdateRequest): Promise<Group> => {
    const response = await apiClient.put<Group>(`/api/organizations/groups/${groupId}`, request);
    return response.data;
  },

  /**
   * Delete a group
   * DELETE /api/organizations/groups/{groupId}
   */
  delete: async (groupId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/groups/${groupId}`);
  },

  /**
   * Generate an invite code for a group
   * POST /api/organizations/groups/{groupId}/invite-code
   */
  generateInviteCode: async (groupId: string, expiryDays: number = 7): Promise<GroupInviteCodeResponse> => {
    const response = await apiClient.post<GroupInviteCodeResponse>(
      `/api/organizations/groups/${groupId}/invite-code?expiryDays=${expiryDays}`
    );
    return response.data;
  },

  /**
   * Revoke a group's invite code
   * DELETE /api/organizations/groups/{groupId}/invite-code
   */
  revokeInviteCode: async (groupId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/groups/${groupId}/invite-code`);
  },

  /**
   * Join a group by invite code
   * POST /api/organizations/groups/join-by-code
   */
  joinByCode: async (inviteCode: string, userId: string): Promise<Membership> => {
    const response = await apiClient.post<Membership>('/api/organizations/groups/join-by-code', { inviteCode, userId });
    return response.data;
  },
};

// ----------------------------------------------------------------------------
// SHARE LINK ENDPOINTS
// ----------------------------------------------------------------------------

export interface ExistingShareSummary {
  id: string;
  expiresAt: string;
  createdAt: string;
  title?: string;
}

export const shareApi = {
  /**
   * Create a shared playlist link
   * POST /api/share
   */
  create: async (playlist: object, ttlDays?: number): Promise<{ id: string; expiresAt: string; updated: boolean }> => {
    const response = await apiClient.post<{ id: string; expiresAt: string; updated: boolean }>('/api/share', {
      playlist,
      ttlDays: ttlDays ?? 7,
    });
    return response.data;
  },

  /**
   * Create a shared playlist link, replacing an existing one to free a slot
   * POST /api/share with replaceShareId
   */
  createWithReplace: async (playlist: object, replaceShareId: string, ttlDays?: number): Promise<{ id: string; expiresAt: string; updated: boolean }> => {
    const response = await apiClient.post<{ id: string; expiresAt: string; updated: boolean }>('/api/share', {
      playlist,
      replaceShareId,
      ttlDays: ttlDays ?? 7,
    });
    return response.data;
  },

  /**
   * Delete a shared playlist link
   * DELETE /api/share/:id
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/share/${encodeURIComponent(id)}`);
  },
};

// ----------------------------------------------------------------------------
// SETLIST TEMPLATE ENDPOINTS
// ----------------------------------------------------------------------------

export const templatesApi = {
  /**
   * List setlist templates for a church, optionally filtered by group
   * GET /api/organizations/churches/{churchId}/setlist-templates
   */
  getByChurch: async (churchId: string, groupId?: string): Promise<SetlistTemplate[]> => {
    const params = groupId ? `?groupId=${groupId}` : '';
    const response = await apiClient.get<SetlistTemplate[]>(
      `/api/organizations/churches/${churchId}/setlist-templates${params}`
    );
    return response.data;
  },

  /**
   * Get a specific template
   * GET /api/organizations/churches/{churchId}/setlist-templates/{templateId}
   */
  getById: async (churchId: string, templateId: string): Promise<SetlistTemplate> => {
    const response = await apiClient.get<SetlistTemplate>(
      `/api/organizations/churches/${churchId}/setlist-templates/${templateId}`
    );
    return response.data;
  },

  /**
   * Create a new setlist template
   * POST /api/organizations/churches/{churchId}/setlist-templates
   */
  create: async (churchId: string, request: CreateSetlistTemplateDto): Promise<SetlistTemplate> => {
    const response = await apiClient.post<SetlistTemplate>(
      `/api/organizations/churches/${churchId}/setlist-templates`,
      request
    );
    return response.data;
  },

  /**
   * Update a setlist template
   * PUT /api/organizations/churches/{churchId}/setlist-templates/{templateId}
   */
  update: async (churchId: string, templateId: string, request: UpdateSetlistTemplateDto): Promise<SetlistTemplate> => {
    const response = await apiClient.put<SetlistTemplate>(
      `/api/organizations/churches/${churchId}/setlist-templates/${templateId}`,
      request
    );
    return response.data;
  },

  /**
   * Delete a setlist template (soft delete)
   * DELETE /api/organizations/churches/{churchId}/setlist-templates/{templateId}
   */
  delete: async (churchId: string, templateId: string): Promise<void> => {
    await apiClient.delete(`/api/organizations/churches/${churchId}/setlist-templates/${templateId}`);
  },
};

// ----------------------------------------------------------------------------
// MEMBER AVAILABILITY ENDPOINTS
// ----------------------------------------------------------------------------

export const availabilityApi = {
  /**
   * Get every group member's availability for a month (team view, read-only).
   * GET /api/organizations/churches/{churchId}/availability?groupId={groupId}&year={year}&month={month}
   */
  getTeam: async (
    churchId: string,
    groupId: string,
    year: number,
    month: number
  ): Promise<MemberAvailabilityDto[]> => {
    const params = new URLSearchParams({
      groupId,
      year: year.toString(),
      month: month.toString(),
    });
    const response = await apiClient.get<MemberAvailabilityDto[]>(
      `/api/organizations/churches/${churchId}/availability?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Admin: set a single member's availability on their behalf. The member is
   * still free to change it in the app and is notified that an admin updated it.
   * PUT /api/organizations/churches/{churchId}/availability/day
   */
  setMemberDay: async (
    churchId: string,
    groupId: string,
    memberId: string,
    year: number,
    month: number,
    day: number,
    status: AvailabilityStatusValue
  ): Promise<MemberAvailabilityDto> => {
    const response = await apiClient.put<MemberAvailabilityDto>(
      `/api/organizations/churches/${churchId}/availability/day`,
      { groupId, year, month, day, status, targetUserId: memberId }
    );
    return response.data;
  },

  /**
   * Admin: place a member on, or take them off, long-term / indefinite leave
   * from a team. While on leave they show as Blocked every day of every month
   * (member app needs no change, no monthly re-marking) and admins can grey
   * them out of the roster builder.
   * PUT /api/organizations/churches/{churchId}/availability/leave
   */
  setMemberLeave: async (
    churchId: string,
    groupId: string,
    memberId: string,
    onLeave: boolean,
    reason?: string
  ): Promise<{ userId: string; onLeave: boolean; leaveReason?: string | null }> => {
    const response = await apiClient.put<{ userId: string; onLeave: boolean; leaveReason?: string | null }>(
      `/api/organizations/churches/${churchId}/availability/leave`,
      { groupId, targetUserId: memberId, onLeave, reason }
    );
    return response.data;
  },
};

// ─── Notification Preferences (per-user email/push toggles) ─────────────────
export interface NotificationChannelPref {
  email: boolean;
  push: boolean;
}
export interface NotificationPreferences {
  categories: Record<string, NotificationChannelPref>;
}

function normalizeNotifPrefs(data: unknown): NotificationPreferences {
  const d = (data ?? {}) as Record<string, unknown>;
  const raw = (d.categories ?? d.Categories ?? {}) as Record<string, Record<string, unknown>>;
  const categories: Record<string, NotificationChannelPref> = {};
  for (const key of Object.keys(raw)) {
    const v = raw[key] ?? {};
    categories[key] = {
      email: (v.email ?? v.Email ?? true) as boolean,
      push: (v.push ?? v.Push ?? true) as boolean,
    };
  }
  return { categories };
}

export const notificationPreferencesApi = {
  get: async (): Promise<NotificationPreferences> => {
    const response = await apiClient.get('/api/notifications/preferences');
    return normalizeNotifPrefs(response.data);
  },
  update: async (prefs: NotificationPreferences): Promise<NotificationPreferences> => {
    const response = await apiClient.put('/api/notifications/preferences', prefs);
    return normalizeNotifPrefs(response.data);
  },
};

/** Mirrors Organizations' HubLinkStatusResponse. */
export interface HubLinkStatus {
  status: 'None' | 'Pending' | 'Linked' | 'Declined';
  hubChurchId: string | null;
  hubChurchName: string | null;
  requestedAt: string | null;
  hasActiveCode: boolean;
}

export interface HubLinkCode {
  /** Grouped for reading aloud, e.g. "4KQ7-M2XP". */
  code: string;
  expiresAt: string;
}

/**
 * Connecting a church to Church Hub.
 *
 * Deliberately two-sided: this side issues a short-lived code, a Church Hub
 * admin redeems it, and this side then approves. The two products keep separate
 * church databases — linking records the agreement, and grants no permissions
 * in either direction.
 */
export const hubLinkApi = {
  get: async (churchId: string): Promise<HubLinkStatus> => {
    const response = await apiClient.get(`/api/organizations/churches/${churchId}/hub-link`);
    return response.data;
  },

  /** Issues a code for a Church Hub admin to redeem. Replaces any previous one. */
  generateCode: async (churchId: string): Promise<HubLinkCode> => {
    const response = await apiClient.post(
      `/api/organizations/churches/${churchId}/hub-link-code`
    );
    return response.data;
  },

  /** Answers a pending request. Church Hub is told either way. */
  decide: async (churchId: string, approved: boolean): Promise<HubLinkStatus> => {
    const response = await apiClient.post(
      `/api/organizations/churches/${churchId}/hub-link/decision`,
      { approved }
    );
    return response.data;
  },
};
